import $ from 'jquery';
import { utils } from '../common/utils';
import type { IDonation, IDonorInfo, IAddress } from '../types';

export class DonationPaymentOptions {
    private $donationFlow: JQuery<HTMLElement>;
    private currentpaymentmethod: string;
    private email: JQuery<HTMLElement>;
    private invoiceemail: JQuery<HTMLElement>;
    private volontaryssn: JQuery<HTMLElement>;
    private mandatoryssn: JQuery<HTMLElement>;
    private donorname: JQuery<HTMLElement>;
    private donororgno: JQuery<HTMLElement>;
    private donorcontact: JQuery<HTMLElement>;
    private donoraddress: JQuery<HTMLElement>;
    private donorpostalcode: JQuery<HTMLElement>;
    private donorcity: JQuery<HTMLElement>;
    private emailcopyreference: string | null;
    private $emailcopylink: JQuery<HTMLElement>;
    private $subscribeNewsletter: JQuery<HTMLElement>;
    private $addressButton: JQuery<HTMLElement>;
    private manualaddress: boolean;
    private isCompany: boolean;
    private currentAddress: IAddress | null = null;

    constructor(donationobject: IDonation, emailcopyreference?: string | null) {
        this.$donationFlow = donationobject.$DonationElement;
        this.currentpaymentmethod = (this.$donationFlow.find('input[name="rbpaymentoptions"]:checked').val() as string) ?? '';
        this.email = this.$donationFlow.find('.useremail');
        this.invoiceemail = this.$donationFlow.find('.userinvoiceemail');
        this.volontaryssn = this.$donationFlow.find('.nonmandtxtpnr');
        this.mandatoryssn = this.$donationFlow.find('.mandtxtpnr');
        this.donorname = this.$donationFlow.find('#txtdonorname');
        this.donororgno = this.$donationFlow.find('#txtorgno');
        this.donorcontact = this.$donationFlow.find('#txtcontactname');
        this.donoraddress = this.$donationFlow.find('#txtaddress');
        this.donorpostalcode = this.$donationFlow.find('#txtpostalcode');
        this.donorcity = this.$donationFlow.find('#txtcity');
        this.emailcopyreference = emailcopyreference ?? null;
        this.$emailcopylink = this.$donationFlow.find('.copy-email');
        this.$subscribeNewsletter = this.$donationFlow.find('input[name=cbnewsletter]');
        this.$addressButton = this.$donationFlow.find('.btn-collect-address');
        this.manualaddress = donationobject.isCompany;
        this.isCompany = donationobject.isCompany;
        this.initPaymentOptionsEvents();
    }

    get Address(): IAddress | null {
        return this.currentAddress;
    }

    get PaymentMethod(): string {
        return this.currentpaymentmethod;
    }

    get DonorInfo(): IDonorInfo {
        return {
            name: this.donorname.val(),
            companycontact: this.donorcontact.val(),
            address: this.donoraddress.val(),
            postalCode: this.donorpostalcode.val(),
            city: this.donorcity.val(),
            identificationNumber: this.getIdentificationNumber(),
            email: this.email.val(),
            invoiceemail: this.invoiceemail.val(),
            newsletter: this.$subscribeNewsletter.is(':checked'),
            addressReference: this.currentAddress?.addressReference,
            manualAddress: this.manualaddress,
            isCompany: this.isCompany
        };
    }

    private getIdentificationNumber(): string | number | string[] | undefined {
        if (this.isCompany) return this.donororgno.val();
        if (this.currentpaymentmethod === 'card') return this.volontaryssn.val();
        return this.mandatoryssn.val();
    }

    paymentValid(): boolean {
        let result = true;
        const useremail = this.email.val() as string;
        if (useremail === '' || !utils.validEmail(useremail)) {
            this.email.addClass('is-invalid');
            result = false;
        } else {
            this.email.removeClass('is-invalid');
        }

        if (this.PaymentMethod === 'card') {
            const volontaryssnval = this.volontaryssn.val() as string;
            if (volontaryssnval !== '' && !utils.validSsn4digitYear(volontaryssnval)) {
                this.volontaryssn.addClass('is-invalid');
                result = false;
            } else {
                this.volontaryssn.removeClass('is-invalid');
            }
        }

        if (this.PaymentMethod === 'invoice') {
            if (!this.isCompany) {
                const mandatoryssnval = this.mandatoryssn.val() as string;
                if (mandatoryssnval === '' || !utils.validSsn4digitYear(mandatoryssnval)) {
                    this.mandatoryssn.addClass('is-invalid');
                    result = false;
                } else {
                    this.mandatoryssn.removeClass('is-invalid');
                }
            }

            if (this.manualaddress || this.isCompany) {
                const fields: Array<{ $el: JQuery<HTMLElement> }> = [
                    { $el: this.donorname },
                    { $el: this.donoraddress },
                    { $el: this.donorpostalcode },
                    { $el: this.donorcity }
                ];
                fields.forEach(({ $el }) => {
                    if ($el.val() === '') { $el.addClass('is-invalid'); result = false; }
                    else $el.removeClass('is-invalid');
                });

                if (this.isCompany) {
                    if (this.donororgno.val() === '') { this.donororgno.addClass('is-invalid'); result = false; }
                    else this.donororgno.removeClass('is-invalid');

                    if (this.donorcontact.val() === '') { this.donorcontact.addClass('is-invalid'); result = false; }
                    else this.donorcontact.removeClass('is-invalid');
                }
            } else if (this.currentAddress !== null && !this.currentAddress.addressReference) {
                this.$donationFlow[0].dispatchEvent(new CustomEvent('donationerror', { detail: { error: 'Det finns ingen adress knuten till gåvan, sök upp din adress igen. ' } }));
                result = false;
            }
        }

        return result;
    }

    private updateAddressControls(): void {
        if (this.manualaddress || this.isCompany) {
            const addressinputs = this.$donationFlow.find('.payment-address input');
            addressinputs.removeAttr('readonly');
            addressinputs.each((_idx, item) => {
                const $item = $(item);
                if (($item.val() as string).indexOf('*') > -1) {
                    $item.val('');
                }
            });
        } else {
            this.$donationFlow.find('.payment-address input').attr('readonly', '');
            if (this.currentAddress?.address) {
                const addr = this.currentAddress.address;
                this.donorname.val(`${addr.firstname ?? ''} ${addr.lastname ?? ''}`.trim());
                this.donoraddress.val(`${addr.address1 ?? ''} ${addr.address2 ?? ''}`.trim());
                this.donorpostalcode.val(`${addr.postalcode ?? ''}`.trim());
                this.donorcity.val(`${addr.city ?? ''}`.trim());
            }
        }
    }

    private initPaymentOptionsEvents(): void {
        if (this.emailcopyreference) {
            this.$emailcopylink.show();
            this.$emailcopylink.on('click', () => {
                const reffield = this.$donationFlow.find(this.emailcopyreference!);
                if (reffield.length > 0) this.email.val(reffield.val() as string);
                return false;
            });

            this.$donationFlow.on('deliveryoptions', (evt: JQuery.TriggeredEvent) => {
                const detail = (evt.originalEvent as CustomEvent).detail as { delivery: string };
                if (detail.delivery === 'printeddelivery') {
                    this.$emailcopylink.fadeOut(200);
                } else {
                    this.$emailcopylink.fadeIn(200);
                }
            });
        } else {
            this.$emailcopylink.hide();
        }

        this.$donationFlow.find('.cbmanualaddress').on('change', (evt) => {
            this.manualaddress = (evt.target as HTMLInputElement).checked;
            this.updateAddressControls();
        });

        this.$donationFlow.find('input[name="rbpaymentoptions"]').on('change', (evt) => {
            this.currentpaymentmethod = (evt.currentTarget as HTMLInputElement).value;
            // Fade the selected payment method's section in and the other out (matches the
            // pre-upgrade behaviour that was lost in the Gulp→Vite/TypeScript migration).
            const $card = this.$donationFlow.find('.payment-card');
            const $invoice = this.$donationFlow.find('.payment-invoice');
            if (this.currentpaymentmethod === 'invoice') {
                $card.fadeOut(200, () => $invoice.fadeIn(200));
            } else {
                $invoice.fadeOut(200, () => $card.fadeIn(200));
            }
            this.updateAddressControls();
        });

        this.$addressButton.on('click', () => {
            if (!utils.validSsn4digitYear(this.mandatoryssn.val() as string)) {
                this.mandatoryssn.addClass('is-invalid');
            } else {
                this.mandatoryssn.removeClass('is-invalid');
                this.$addressButton.prop('disabled', 'disabled');
                utils.getAddressBySsn(this.mandatoryssn.val() as string)
                    .fail((response: JQuery.jqXHR, status: string) => {
                        const json = response.responseJSON as { error?: string } | undefined;
                        let error = json?.error ?? status;
                        if (error === 'error') {
                            error = 'Ett fel uppstod när adressen skulle hämtas, vänligen försök igen, eller ange adressen manuellt';
                        }
                        this.$donationFlow[0].dispatchEvent(new CustomEvent('donationerror', { detail: { error } }));
                    })
                    .done((response: IAddress & { error?: string }) => {
                        if (response.error) {
                            this.$donationFlow[0].dispatchEvent(new CustomEvent('donationerror', { detail: { error: response.error } }));
                        }
                        this.currentAddress = response;
                        this.updateAddressControls();
                    })
                    .always(() => {
                        this.$addressButton.removeAttr('disabled');
                    });
            }
        });
    }
}
